import sortBy from 'lodash/sortBy'
import he from 'he'
import { orderBy } from 'lodash'
import {
  MagentoConfigurableVariant,
  MagentoProductInterface,
  MagentoSimpleProduct,
  MagentoStoreConfig,
  ProductCustomAttribute,
  ProductData,
  VisitureProductCustomAttribute
} from 'lib/types'
import { ProductAttributeList } from 'lib/core/data/common/productAttributeList/types'
import { COMMA, ATTRIBUTE_TYPE, EMPTY_STRING } from 'lib/core/resources/constants'
import { stripHtmlTags } from 'lib/core/resources/helpers'

const { MULTISELECT, SELECT, BOOLEAN, TEXTAREA } = ATTRIBUTE_TYPE
const POSITION_KEY = 'position'

export const normalizeProductCustomAttributes = (customAttributesJSON: string, productAttributeList?: ProductAttributeList) => {
  const customAttributeList: ProductCustomAttribute[] = []
  const customAttributesIndex: Record<string, string[]> = {}

  if (productAttributeList) {
    let customAttributeData: VisitureProductCustomAttribute[] = []
    try {
      customAttributeData = customAttributesJSON ? (JSON.parse(customAttributesJSON) || []) : []
    } catch {}

    customAttributeData.forEach(customAttribute => {
      const { attribute_code: attributeCode, value } = customAttribute
      const attr = productAttributeList[attributeCode]
      if (attr) {
        const { frontendInput, defaultLabel, options } = attr

        const attrValue: string[] = []

        switch (frontendInput) {
          case MULTISELECT:
          case SELECT: {
            value.split(COMMA).forEach(attrOption => {
              const value = options?.[attrOption]
              if (value) {
                attrValue.push(he.decode(value))
              }
            })
            break
          }
          case BOOLEAN: {
            const parsed = value && !!parseInt(value, 10)
            attrValue.push(parsed ? value : EMPTY_STRING)
            break
          }
          case TEXTAREA: {
            attrValue.push(value && he.decode(value ?? ''))
            break
          }
          default: {
            attrValue.push(value && he.decode(stripHtmlTags(value) ?? ''))
          }
        }

        customAttributesIndex[attributeCode] = attrValue

        customAttributeList.push({
          code: attributeCode,
          label: defaultLabel,
          value: attrValue
        })
      }
    })
  }

  return { customAttributeList, customAttributesIndex }
}

export const normalizeProduct = (params: {
  product: MagentoSimpleProduct & Partial<MagentoProductInterface>
  productAttributeList: ProductAttributeList | undefined
  storeConfig: MagentoStoreConfig | undefined
}): ProductData => {
  const { storeConfig, productAttributeList, product } = params
  const {
    configurable_options: configurableOptions,
    canonical_url: canonicalUrl,
    url_rewrites: _urlRewrites,
    description,
    is_default_selection: isDefault,
    id,
    image,
    small_image: imageSmall,
    thumbnail: imageThumbnail,
    media_gallery_entries: mediaGalleryItems,
    name,
    new_from_date: newFromDate,
    new_to_date: newToDate,
    meta_description: metaDescription,
    meta_keyword: metaKeyword,
    meta_title: metaTitle,
    price,
    price_range: priceRange,
    product_links: productLinks,
    pwa_layout: layout,
    short_description: shortDescription,
    stock_status: stockStatus,
    sku,
    status,
    url_key: urlKey,
    review_summary: reviewSummary,
    variants,
    visiture_custom_attributes: customAttributesJSON,
    categories,
    giftcard_amounts: giftCardAmounts,
    crosssell_products: magentoCrossellProducts,
    upsell_products: magentoUpsellProducts,
    related_products: magentoRelatedProducts,
    only_x_left_in_stock: onlyXLeftInStock,
    options,
    __typename: typeName,
    items: groupedProducts,
    ...rest
  } = product

  const { customAttributeList, customAttributesIndex } = normalizeProductCustomAttributes(customAttributesJSON, productAttributeList)

  const categoriesIds = categories?.reduce((obj: Record<string, boolean>, category) => {
    obj[category.id] = true
    return obj
  }, {})

  const urlRewrites = _urlRewrites?.map(el => el.url) ?? []

  const productData: ProductData = {
    ...rest,
    id,
    sku,
    urlRewrites,
    canonicalUrl,
    status,
    name,
    price,
    priceRange,
    categories: categories ?? [],
    configurableOptions: configurableOptions ? sortBy(configurableOptions, POSITION_KEY) : [],
    descriptionHtml: description?.html ?? EMPTY_STRING,
    mediaGalleryItems: mediaGalleryItems ? sortBy(mediaGalleryItems, POSITION_KEY) : [],
    newFromDate,
    newToDate,
    metaDescription,
    metaKeyword,
    metaTitle,
    productLinks: productLinks ?? [],
    image: {
      default: image,
      thumbnail: imageThumbnail,
      small: imageSmall
    },
    urlKey,
    isDefault: isDefault ?? false,
    isInStock: stockStatus === 'IN_STOCK',
    layout,
    stockStatus,
    reviewSummary,
    shortDescriptionHtml: shortDescription?.html ?? EMPTY_STRING,
    customAttributeList,
    customAttribute: customAttributesIndex,
    variants: variants ? variants.map((variant) => normalizeVariants({
      variant,
      productAttributeList,
      storeConfig
    })) : [],
    breadcrumb: categories?.find(category => category.breadcrumbs)?.breadcrumbs ?? undefined,
    categoryMap: categoriesIds ?? undefined,
    crosssellProducts: magentoCrossellProducts?.map(product => normalizeProduct({ product, productAttributeList, storeConfig })),
    // TODO: Fix products query - upsell products price are coming null https://expandlab.atlassian.net/browse/MINERVA-1024
    upsellProducts: magentoUpsellProducts?.filter(product => product.price).map(product => normalizeProduct({
      product,
      productAttributeList,
      storeConfig
    })),
    // TODO: Fix products query - related products price are coming null https://expandlab.atlassian.net/browse/MINERVA-1024
    relatedProducts: magentoRelatedProducts?.filter(product => product.price).map(product => normalizeProduct({
      product,
      productAttributeList,
      storeConfig
    })),
    giftCardAmounts,
    groupedProducts: groupedProducts ? orderBy(groupedProducts, ['position'], ['asc']) : [],
    options,
    onlyXLeftInStock,
    typeName,
    getUrlPath: () => {
      const pathnameLength = window.location.pathname.length
      const path = urlRewrites.find(path => path.substr(pathnameLength) === urlKey) ?? `${urlKey}${storeConfig?.product_url_suffix ?? ''}`
      return `/${path}`
    }
  }

  return productData
}

export const normalizeVariants = (params: {
  variant: MagentoConfigurableVariant
  productAttributeList?: ProductAttributeList
  storeConfig?: MagentoStoreConfig
}) => {
  const { storeConfig, productAttributeList, variant } = params
  return {
    product: normalizeProduct({
      product: variant.product,
      productAttributeList,
      storeConfig
    }),
    attributes: variant.attributes
  }
}
